import { Link } from 'react-router-dom';
import {
  ShieldCheck,
  Lock,
  Server,
  Cpu,
  FileText,
  Activity,
  Mail,
  ScrollText,
  Network,
  Database,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Globe2,
} from 'lucide-react';
import { LegalLayout } from '../components/LegalLayout';
import '../legal.css';

interface TrustLink {
  to: string;
  external?: boolean;
  icon: typeof ShieldCheck;
  title: string;
  blurb: string;
}

interface TrustSection {
  heading: string;
  intro: string;
  cards: TrustLink[];
}

const SECTIONS: TrustSection[] = [
  {
    heading: 'Security posture',
    intro: 'How Rynora is built, hosted, and defended — the artefacts a CISO or vCISO reviews.',
    cards: [
      {
        to: '/security',
        icon: ShieldCheck,
        title: 'Security overview',
        blurb: 'Hosting, encryption, authn / authz, ops controls. The page security teams read first.',
      },
      {
        to: '/.well-known/security.txt',
        external: true,
        icon: FileText,
        title: 'security.txt (RFC 9116)',
        blurb: 'Machine-readable vulnerability-reporting policy. Pen-test scope and contact addresses.',
      },
      {
        to: '/status',
        icon: Activity,
        title: 'Status & uptime',
        blurb: 'Real-time component status plus historical incident timeline.',
      },
    ],
  },
  {
    heading: 'Privacy & data',
    intro: 'What we collect, why, where it lives, who else sees it, and how long we keep it.',
    cards: [
      {
        to: '/privacy',
        icon: Lock,
        title: 'Privacy policy',
        blurb: 'Plain-English summary up top, full GDPR / CPRA text below. Retention table inline.',
      },
      {
        to: '/trust/subprocessors',
        icon: Network,
        title: 'Sub-processors',
        blurb:
          'Every third-party that processes customer data, with jurisdiction, purpose, and transfer mechanism. Required by every DPA.',
      },
      {
        to: '/trust/regions',
        icon: Globe2,
        title: 'Regions & data residency',
        blurb:
          'Which Rynora SaaS regions are live today, which are in design-partner alpha, and which are on the roadmap. Honest jurisdictions — no vapour.',
      },
      {
        to: '/cookies',
        icon: Database,
        title: 'Cookie policy',
        blurb: 'Strictly-necessary vs functional vs analytics. The controls you have in-product.',
      },
    ],
  },
  {
    heading: 'AI transparency',
    intro: 'The 2026 hot section. Buyers run a separate "AI committee" gate; these are the artefacts that gate clears.',
    cards: [
      {
        to: '/trust/ai',
        icon: Cpu,
        title: 'AI Bill of Materials (AIBOM)',
        blurb:
          'Which Rynora feature uses which LLM provider, which model, and the no-training-on-your-data contractual statement.',
      },
      {
        to: '/sovereign-ai',
        icon: ShieldCheck,
        title: 'Sovereign / air-gapped tier',
        blurb:
          'Zero outbound LLM traffic. Ollama + vLLM adapters run inside the buyer perimeter. Closes the FedRAMP / HIPAA / pre-IPO-fintech gate that no commercial rival can match.',
      },
    ],
  },
  {
    heading: 'Operational commitments',
    intro: 'What we promise, with consequences if we miss.',
    cards: [
      {
        to: '/trust/sla',
        icon: CheckCircle2,
        title: 'SLA & service credits',
        blurb: 'Per-tier uptime targets, what counts as downtime, and the service-credit ladder when we miss.',
      },
      {
        to: '/trust/deprecation',
        icon: Clock,
        title: 'Deprecation policy',
        blurb: '12-month notice on breaking API changes. How removals are communicated and migrated.',
      },
    ],
  },
  {
    heading: 'Self-service & sovereign deployment',
    intro: 'For buyers whose compliance posture forbids running on a shared SaaS.',
    cards: [
      {
        to: '/sovereign-ai',
        icon: Server,
        title: 'Air-gapped reference architecture',
        blurb: 'Run the entire Rynora control plane + runners + LLM inside your VPC / on-prem subnet. No egress.',
      },
      {
        to: '/docs/self-hosting',
        icon: ScrollText,
        title: 'Self-hosting docs',
        blurb: 'The Compose + Caddy stack. What ports, what credentials, what backups.',
      },
    ],
  },
];

const QUESTIONNAIRE_STEPS = [
  {
    title: 'Already public on this page',
    blurb:
      'Security overview, privacy policy, sub-processor list, AIBOM, SLA, deprecation policy, status page. Read straight through and you have answers to about 70% of a standard CAIQ-Lite / SIG-Lite.',
  },
  {
    title: 'Available under NDA',
    blurb:
      'SOC 2 Type II report (in progress — observation window started), independent pen-test report (annual), DPA / MSA / BAA templates, insurance certificates. Email us and we will route via Whistic / OneTrust if you have a TPRM tool.',
  },
  {
    title: 'Built into the product',
    blurb:
      'SCIM 2.0, SSO (SAML / OIDC) with JIT provisioning, RBAC, audit log with FTS + CSV export + before/after diff, per-org run retention, encrypted backups, CI-key rotation, PII / compliance scanner with SOC 2 + HIPAA evidence-pack PDF export. Demos on request.',
  },
];

export function TrustCenterPage() {
  return (
    <LegalLayout
      title="Trust Center"
      updated="June 1, 2026"
      summary="The single page enterprise security / privacy / procurement teams should read before running a full questionnaire. Every artefact below either lives on this page or is available under NDA."
      needsReview={false}
    >
      <p>
        Rynora is a governed Quality Intelligence platform for change-aware Quality Engineering. It touches your app
        credentials, captures screenshots and network traffic during runs, and (optionally) calls third-party LLM
        providers for authoring and analysis assists. Because of that blast radius we publish more of our security and
        privacy posture than most early-stage SaaS. The goal is that your CISO, DPO, and AI committee can each answer
        the questions they care about without a 30-day procurement cycle.
      </p>
      <p>
        For everything that needs an NDA (SOC 2 report, pen-test report, DPA / MSA / BAA), email{' '}
        <a href="mailto:trust@rynora.app">trust@rynora.app</a>. We respond within one business day.
      </p>

      <h2>How buyers typically use this page</h2>
      <ul>
        {QUESTIONNAIRE_STEPS.map((step) => (
          <li key={step.title}>
            <strong>{step.title}.</strong> {step.blurb}
          </li>
        ))}
      </ul>

      {SECTIONS.map((section) => (
        <section key={section.heading} className="trust-section">
          <h2>{section.heading}</h2>
          <p className="trust-section-intro">{section.intro}</p>
          <div className="trust-card-grid">
            {section.cards.map((card) => {
              const Icon = card.icon;
              const inner = (
                <>
                  <span className="trust-card-icon" aria-hidden="true">
                    <Icon size={20} />
                  </span>
                  <span className="trust-card-body">
                    <span className="trust-card-title">{card.title}</span>
                    <span className="trust-card-blurb">{card.blurb}</span>
                  </span>
                </>
              );

              if (card.external) {
                return (
                  <a key={card.title} className="trust-card" href={card.to} rel="noreferrer">
                    {inner}
                  </a>
                );
              }
              return (
                <Link key={card.title} className="trust-card" to={card.to}>
                  {inner}
                </Link>
              );
            })}
          </div>
        </section>
      ))}

      <h2>Audits, certifications, and reports</h2>
      <p>
        Rynora is an early-stage company. We are transparent about what we have and what is in flight so you can size
        the gap against your procurement bar before you spend cycles on us.
      </p>
      <table>
        <thead>
          <tr>
            <th>Artefact</th>
            <th>Status</th>
            <th>How to request</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>SOC 2 Type II</td>
            <td>In progress — observation window started Q2 2026. Report expected H2 2026.</td>
            <td>
              Bridge letter on request via <a href="mailto:trust@rynora.app">trust@rynora.app</a>.
            </td>
          </tr>
          <tr>
            <td>ISO 27001</td>
            <td>Not started. Most NA buyers accept SOC 2 in lieu; EU / APAC buyers — talk to us about timeline.</td>
            <td>—</td>
          </tr>
          <tr>
            <td>Independent penetration test</td>
            <td>
              Continuous DAST (OWASP ZAP) + SAST (semgrep) + dep scan (npm audit) + secret scan (gitleaks) in CI today.
              Annual third-party pen-test cadence starting Q3 2026.
            </td>
            <td>
              Latest CI security-scan summary on request via <a href="mailto:trust@rynora.app">trust@rynora.app</a>.
            </td>
          </tr>
          <tr>
            <td>HIPAA BAA</td>
            <td>
              Engineering posture (encryption, audit log, access controls, retention) is BAA-compatible. Sovereign tier
              covers HIPAA buyers who cannot accept any commercial cloud transfer. Template BAA in legal review.
            </td>
            <td>
              Email <a href="mailto:trust@rynora.app">trust@rynora.app</a>.
            </td>
          </tr>
          <tr>
            <td>PCI DSS</td>
            <td>
              Out of scope by design — Rynora does not process card data; Stripe handles all card surfaces under their
              own PCI scope.
            </td>
            <td>Attestation letter on request.</td>
          </tr>
          <tr>
            <td>GDPR DPA + SCCs / UK IDTA</td>
            <td>Template DPA in legal review. EU SCCs + UK IDTA + Swiss-US framework appended.</td>
            <td>
              Email <a href="mailto:trust@rynora.app">trust@rynora.app</a>; signed copy returned same week.
            </td>
          </tr>
        </tbody>
      </table>

      <h2>Vulnerability disclosure</h2>
      <p>
        We follow <a href="/.well-known/security.txt">security.txt</a> (RFC 9116). Report vulnerabilities to{' '}
        <a href="mailto:security@rynora.app">security@rynora.app</a>; we acknowledge within 24 hours and triage within
        72. We publish fixes and post-mortems via{' '}
        <a href="https://github.com/rynoratech/rynora/security/advisories" rel="noreferrer" target="_blank">
          GitHub security advisories
        </a>
        . A formal bug bounty (HackerOne / Intigriti) is planned — current state is a Vulnerability Disclosure Program
        (VDP) with safe-harbour language in the security policy.
      </p>

      <div className="trust-contact-card" role="note">
        <Mail size={20} aria-hidden="true" />
        <div>
          <strong>Procurement intake:</strong> <a href="mailto:trust@rynora.app">trust@rynora.app</a>
          <br />
          <span className="trust-contact-card-blurb">
            One-business-day response. Send your CAIQ / SIG / custom questionnaire as an attachment and we will return
            it with links into this page wherever possible.
          </span>
        </div>
      </div>

      <h2>What this page is not</h2>
      <p className="trust-disclaimer">
        <AlertTriangle size={14} aria-hidden="true" style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />
        This Trust Center summarises Rynora's posture but does not by itself replace a Data Processing Agreement, Master
        Services Agreement, BAA, or any other contractual commitment. The artefacts available under NDA are the
        canonical source of truth for contractual purposes.
      </p>
    </LegalLayout>
  );
}
